import {
  useState, useEffect, useMemo, useCallback,
} from 'react';

/**
 * Hook to get the top position of sticky elements
 * @param {string[]} elems - The elements to get the top position of
 * @param {object} [options]
 * @param {boolean} [options.enabled=true] - When false, skips DOM observation until enabled
 * @returns {{ stickyHeight: number, remainingHeight: number }}
 */
const useStickyTopElems = (elems, { enabled = true } = {}) => {
  const [elementSizes, setElementSizes] = useState({});
  const [viewportHeight, setViewportHeight] = useState(typeof window !== 'undefined' ? window.innerHeight : 0);
  const stableElems = useMemo(() => elems, [JSON.stringify(elems)]);

  const updateSizes = useCallback(() => {
    /** @type {{ [key: string]: number }} */
    const newSizes = {};
    stableElems.forEach((elemId) => {
      const element = typeof document !== 'undefined' ? document.getElementById(elemId) : null;
      newSizes[elemId] = element?.offsetHeight || 0;
    });
    setElementSizes(newSizes);
  }, [stableElems]);

  /**
   * Setup ResizeObserver for automatic updates
   */
  useEffect(() => {
    if (!stableElems.length || !enabled) return undefined;
    let cleanup = () => {};
    if (typeof ResizeObserver !== 'undefined' && typeof document !== 'undefined') {
      const ro = new ResizeObserver(updateSizes);
      stableElems.forEach((elemId) => {
        const element = document.getElementById(elemId);
        if (element) {
          ro.observe(element);
        }
      });
      cleanup = () => ro.disconnect();
    }

    updateSizes();

    return cleanup;
  }, [stableElems, updateSizes, enabled]);

  /**
   * Listen to window resize to update viewport height and re-measure sizes
   */
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
      if (enabled) {
        updateSizes();
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [updateSizes, enabled]);

  /**
   * Calculate total height from cached sizes
   */
  const stickyHeight = useMemo(() => Object.values(elementSizes).reduce((acc, size) => acc + size, 0), [elementSizes]);
  const remainingHeight = useMemo(() => Math.max(0, viewportHeight - stickyHeight), [viewportHeight, stickyHeight]);

  return { stickyHeight, remainingHeight };
};

export default useStickyTopElems;
